import React from "react";
import { Building2, ExternalLink, Linkedin, MapPin, Sparkles, User, Users, X } from "lucide-react";
import CustomDrawer from "@/shared/components/ui/Drawer/CustomDrawer";
import { PrimaryButton } from "@/shared/components/ui/Button/PrimaryButton";
import SecondaryButton from "@/shared/components/ui/Button/SecondaryButton";
import SquircleIcon from "@/shared/components/ui/SquircleIcon";
import { DRAWER_WIDTH } from "@/features/accounts/components/AccountDiscovery/results/config";
import { getInitials, getSafeUrl } from "@/features/accounts/components/AccountDiscovery/results/utils";
import { SourceLink } from "@/features/accounts/components/AccountDiscovery/results/AccountDetailsDrawer";
import { emailQuality, getPersonCompany, getPersonFullName, getPersonLocation } from "../../utils/prospectUtils";
import type { ProspectItem } from "../../types";

const CustomDrawerComponent = CustomDrawer as React.ComponentType<any>;
const PrimaryButtonComponent = PrimaryButton as React.ComponentType<any>;
const SecondaryButtonComponent = SecondaryButton as React.ComponentType<any>;
const SquircleIconComponent = SquircleIcon as React.ComponentType<any>;

const hostOf = (url: string) => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};

/** Section title with a small tinted icon, so each block is easy to find when scanning. */
const Section = ({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) => (
  <section className="flex flex-col gap-3">
    <div className="flex items-center gap-2">
      <span className="flex items-center justify-center w-6 h-6 rounded-md bg-secondary-light-transparent dark:bg-secondary-transparent text-secondary">
        {icon}
      </span>
      <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-100 font-sans">{title}</h4>
    </div>
    {children}
  </section>
);

/** One fact for the strip under the header. */
const Fact = ({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) =>
  children ? (
    <span className="inline-flex items-center gap-1.5 max-w-full rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2.5 py-1 text-xs text-gray-600 dark:text-gray-300">
      <span className="text-gray-400 shrink-0">{icon}</span>
      <span className="truncate">{children}</span>
    </span>
  ) : null;

/** Label / value rows in one card. Rows without a value are left out. */
const DetailsCard = ({ rows }: { rows: Array<[string, React.ReactNode]> }) => (
  <dl className="rounded-xl border border-gray-200 dark:border-gray-800 divide-y divide-gray-100 dark:divide-gray-800">
    {rows
      .filter(([, value]) => value)
      .map(([label, value]) => (
        <div key={label} className="grid grid-cols-[96px_1fr] gap-3 px-3.5 py-2.5 text-sm">
          <dt className="text-gray-400 dark:text-gray-500">{label}</dt>
          <dd className="text-gray-800 dark:text-gray-200 min-w-0 break-words">{value}</dd>
        </div>
      ))}
  </dl>
);

const LinkValue = ({ href, label }: { href: string; label: string }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-secondary hover:underline">
    <span className="truncate">{label}</span>
    <ExternalLink size={12} className="shrink-0" />
  </a>
);

interface ProspectDetailsDrawerProps {
  prospect: ProspectItem | null;
  isImported: boolean;
  isImporting: boolean;
  onImport: (p: ProspectItem) => void;
  onClose: () => void;
}

/** Side panel for one contact: who they are, why they matched (with sources), company and contact. */
export default function ProspectDetailsDrawer({
  prospect,
  isImported,
  isImporting,
  onImport,
  onClose,
}: ProspectDetailsDrawerProps) {
  const fullName = prospect ? getPersonFullName(prospect) : "";
  const org = prospect?.organization;
  const website = getSafeUrl(org?.website);
  const linkedin = getSafeUrl(prospect?.linkedin_url);
  const location = prospect && (prospect.location || prospect.city || prospect.country) ? getPersonLocation(prospect) : "";
  const evidence = (Array.isArray(prospect?.evidence) ? prospect.evidence : []).filter((e: any) => getSafeUrl(e?.source_url));

  return (
    <CustomDrawerComponent isOpen={Boolean(prospect)} onClose={onClose} width={DRAWER_WIDTH}>
      {prospect && (
        <div className="flex flex-col h-full">
          <div className="flex flex-col gap-3 p-4 border-b border-gray-200 dark:border-gray-800">
            <div className="flex items-center gap-3">
              <SquircleIconComponent className="w-[38px] h-[38px]">
                <span className="text-base font-medium text-white">{getInitials(fullName)}</span>
              </SquircleIconComponent>
              <div className="flex flex-col gap-0.5 min-w-0">
                <h3 className="text-base font-medium text-gray-800 dark:text-gray-100 font-sans truncate">{fullName}</h3>
                <span className="text-xs text-gray-500 dark:text-gray-400 font-sans truncate">
                  {prospect.title || "Contact"}
                  {org?.name ? ` · ${org.name}` : ""}
                </span>
              </div>
              <SecondaryButtonComponent
                title=""
                aria-label="Close details"
                className="!w-8 !h-8 !min-w-8 !p-0 !gap-0 rounded-lg flex items-center ml-auto justify-center border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                icon={<X size={18} className="text-gray-500" strokeWidth={2.5} />}
                onClick={onClose}
              />
            </div>
            <div className="flex flex-wrap gap-1.5">
              <Fact icon={<Building2 size={12} />}>{getPersonCompany(prospect)}</Fact>
              <Fact icon={<MapPin size={12} />}>{location}</Fact>
              <Fact icon={<Users size={12} />}>{org?.company_size}</Fact>
            </div>
          </div>

          {/* The body scrolls; the header and footer stay put. */}
          <div className="flex flex-1 min-h-0 flex-col gap-6 p-4 overflow-y-auto">
            {(prospect.reason || evidence.length > 0 || prospect.closest_match) && (
              <Section icon={<Sparkles size={14} />} title="Why it matched">
                {prospect.closest_match && (
                  <p className="rounded-xl border border-amber-200 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-950/30 px-3.5 py-2.5 text-sm text-amber-800 dark:text-amber-300 leading-relaxed">
                    <span className="font-medium">Closest match:</span>{" "}
                    {prospect.match_note || "Nobody at this company holds the exact title you searched for."}
                  </p>
                )}
                {prospect.reason && (
                  <p className="rounded-xl border-l-[3px] border-secondary bg-secondary-light-transparent dark:bg-secondary-transparent px-3.5 py-3 text-sm text-gray-700 dark:text-gray-200 leading-relaxed">
                    {prospect.reason}
                  </p>
                )}
                {/* Same compact source list as the account drawer: summary, then a small link. */}
                {evidence.map((item: any, index: number) => {
                  const href = getSafeUrl(item.source_url) as string;
                  return (
                    <span
                      key={`${href}-${index}`}
                      className="flex flex-col gap-2 border-b border-dotted border-gray-200 dark:border-gray-700 pb-3 last:border-b-0 last:pb-0"
                    >
                      {item.summary && <span className="text-sm text-gray-600 dark:text-gray-300 font-sans">{item.summary}</span>}
                      <SourceLink href={href} label={item.title || hostOf(href)} />
                    </span>
                  );
                })}
              </Section>
            )}

            <Section icon={<Building2 size={14} />} title="Company">
              <DetailsCard
                rows={[
                  ["Name", getPersonCompany(prospect)],
                  ["Website", website ? <LinkValue href={website} label={org?.domain || hostOf(website)} /> : null],
                  ["Industry", org?.industry],
                  ["Size", org?.company_size],
                  ["Location", location],
                ]}
              />
            </Section>

            <Section icon={<User size={14} />} title="Contact">
              <DetailsCard
                rows={[
                  [
                    "Email",
                    prospect.email ||
                      (prospect.availability?.email ? (
                        <span>
                          {emailQuality(prospect) === "verified"
                            ? "Verified"
                            : emailQuality(prospect) === "unverified"
                              ? "Found, not verified (may bounce)"
                              : "Found"}
                          <span className="text-gray-400">. Revealed when you add this contact</span>
                        </span>
                      ) : (
                        <span className="text-gray-400">{prospect.availability ? "Not available" : "Revealed when you add this contact"}</span>
                      )),
                  ],
                  [
                    "Phone",
                    prospect.phone ||
                      (prospect.availability?.phone ? (
                        <span>
                          Available<span className="text-gray-400">, revealed on import</span>
                        </span>
                      ) : null),
                  ],
                  [
                    "LinkedIn",
                    linkedin ? (
                      <span className="inline-flex items-center gap-1.5">
                        <Linkedin size={13} className="text-secondary shrink-0" />
                        <LinkValue href={linkedin} label="View profile" />
                      </span>
                    ) : null,
                  ],
                ]}
              />
            </Section>

            {prospect.headline && (
              <Section icon={<User size={14} />} title="Professional summary">
                <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{prospect.headline}</p>
              </Section>
            )}
          </div>

          <div className="shrink-0 border-t border-gray-200 dark:border-gray-800 p-4">
            <PrimaryButtonComponent
              startContent={false}
              onClick={() => onImport(prospect)}
              isLoading={isImporting}
              disabled={isImported || isImporting}
              className="w-full"
            >
              {isImported ? "Already in Contacts" : "Import to Contacts"}
            </PrimaryButtonComponent>
          </div>
        </div>
      )}
    </CustomDrawerComponent>
  );
}
